import React, { useEffect, useState } from 'react';
import { downloadFile, downloadFileAttachment } from '../../../services/fileApi';
import DocxPreview from '../../Common/DocxPreview';
import LoadingSpinner from '../../Common/LoadingSpinner';
import { toast } from 'react-hot-toast';

export default function TemplatePreviewModal({ file, isOpen, onClose }) {
  const [blobUrl, setBlobUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fileName = file?.fileName || file?.name || file?.originalName || 'document';
  const fileId = file?.fileId || (() => {
    const match = String(file?.url || '').match(/\/download\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : '';
  })();
  const fileUrl = file?.url || '';

  const isDocx = /\.docx$/i.test(fileName) || /\.docx$/i.test(fileUrl);
  const isPdf = /\.pdf$/i.test(fileName) || /\.pdf$/i.test(fileUrl);
  const isImage = /\.(png|jpe?g|webp|gif|svg)$/i.test(fileName) || /\.(png|jpe?g|webp|gif|svg)$/i.test(fileUrl);

  useEffect(() => {
    if (!isOpen || (!fileId && !fileUrl)) {
      setBlobUrl('');
      setError(null);
      setLoading(false);
      return;
    }

    let active = true;
    let createdUrl = '';

    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);

        if (fileId) {
          const res = await downloadFile(fileId);
          if (!active) return;
          const mime = isDocx
            ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            : isPdf
            ? 'application/pdf'
            : res.data?.type || 'application/octet-stream';
          const blob = new Blob([res.data], { type: mime });
          createdUrl = URL.createObjectURL(blob);
          setBlobUrl(createdUrl);
        } else if (fileUrl) {
          if (isDocx || isPdf || isImage) {
            const resp = await fetch(fileUrl);
            if (!resp.ok) throw new Error(`Failed to load file (${resp.status})`);
            const blobData = await resp.blob();
            if (!active) return;
            const mime = isDocx
              ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              : isPdf
              ? 'application/pdf'
              : blobData.type || 'application/octet-stream';
            const blob = new Blob([blobData], { type: mime });
            createdUrl = URL.createObjectURL(blob);
            setBlobUrl(createdUrl);
          } else {
            setBlobUrl(fileUrl);
          }
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to load document for preview:', err);
        if (active) {
          let errorMsg = 'Failed to load document preview';
          if (err?.response?.data instanceof Blob) {
            try {
              const text = await err.response.data.text();
              const json = JSON.parse(text);
              errorMsg = json.message || json.error || errorMsg;
            } catch (_) {}
          } else if (err?.response?.data?.message) {
            errorMsg = err.response.data.message;
          } else if (err?.message) {
            errorMsg = err.message;
          }
          setError(errorMsg);
          setLoading(false);
        }
      }
    };

    loadContent();

    return () => {
      active = false;
      if (createdUrl && createdUrl.startsWith('blob:')) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [isOpen, fileId, fileUrl, isDocx, isPdf, isImage]);

  if (!isOpen) return null;

  const handleDownload = async () => {
    try {
      if (blobUrl) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success('Download started');
      } else if (fileId) {
        const mime = isDocx
          ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          : isPdf
          ? 'application/pdf'
          : 'application/octet-stream';
        await downloadFileAttachment(fileId, fileName, mime);
      } else if (fileUrl) {
        const a = document.createElement('a');
        a.href = fileUrl;
        a.download = fileName;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      toast.error('Failed to download document');
    }
  };

  const handleOpenNewTab = () => {
    if (blobUrl) {
      window.open(blobUrl, '_blank');
    } else if (fileId) {
      window.open(`/api/files/download/${fileId}?inline=true`, '_blank');
    } else if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-xs transition-opacity"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl h-full max-h-[92vh] flex flex-col bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white shrink-0">
          <div className="flex items-center gap-2.5 min-w-0 pr-4">
            <span className="text-xl">
              {isDocx ? '📝' : isPdf ? '📄' : isImage ? '🖼️' : '📎'}
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold truncate text-white" title={fileName}>
                {fileName}
              </h3>
              <p className="text-[11px] text-gray-400">Template Document Preview</p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {blobUrl && (
              <button
                type="button"
                onClick={handleOpenNewTab}
                className="px-2.5 py-1 text-xs font-medium text-gray-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded transition-colors flex items-center gap-1"
                title="Open in new tab"
              >
                <span>New Tab</span>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
            )}

            <button
              type="button"
              onClick={handleDownload}
              className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded transition-colors flex items-center gap-1.5 shadow-xs"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Download</span>
            </button>

            <button
              type="button"
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-white hover:bg-slate-800 rounded transition-colors ml-1"
              title="Close (Esc)"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center relative min-h-0">
          {loading && (
            <div className="flex flex-col items-center gap-2 p-8">
              <LoadingSpinner size="lg" />
              <p className="text-xs text-gray-500 font-medium">Loading document preview…</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center p-8 text-center max-w-md">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-3">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h4 className="text-sm font-semibold text-gray-800 mb-1">Preview Unavailable</h4>
              <p className="text-xs text-gray-500 mb-4">{error}</p>
              <button
                type="button"
                onClick={handleDownload}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-md shadow-xs flex items-center gap-2"
              >
                <span>Download Document Directly</span>
              </button>
            </div>
          )}

          {!loading && !error && blobUrl && (
            <>
              {isDocx ? (
                <div className="w-full h-full overflow-auto bg-slate-100">
                  <DocxPreview fileUrl={blobUrl} />
                </div>
              ) : isPdf ? (
                <iframe
                  src={blobUrl}
                  title={fileName}
                  className="w-full h-full bg-white border-0"
                />
              ) : isImage ? (
                <div className="p-4 flex items-center justify-center w-full h-full">
                  <img
                    src={blobUrl}
                    alt={fileName}
                    className="max-w-full max-h-[80vh] object-contain rounded shadow-xs"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 text-center max-w-sm">
                  <div className="text-4xl mb-3">📄</div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">Preview not available for this file type</h4>
                  <p className="text-xs text-gray-500 mb-4">You can download the file to view it on your device.</p>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-md shadow-xs flex items-center gap-2"
                  >
                    <span>Download File</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
